/* NAO-7: ring-size picker as a custom dropdown over Dawn's radio inputs.
   The radios stay the source of truth — VariantSelects reads `input:checked`
   and product-info.js re-renders this whole element on every change, so no
   state is kept here: connectedCallback derives the trigger text from the DOM.
   ponytail: no focus restoration after the section re-render (the element is
   replaced wholesale) except for the sizer toggle below; add a focus-handoff
   in product-info.js if it matters elsewhere. */
if (!customElements.get('size-dropdown')) {
  // Dawn replaces <variant-selects> after every change, which would slam the
  // panel shut mid arrow-key browse. Remember across the swap that the user
  // was navigating by keyboard so the fresh element reopens itself.
  // ponytail: module-level, keyed by panel id — a page can carry several of
  // these (Canele has two), and only the one being browsed should reopen.
  let reopenFor = null;
  // NAO-97 events (BUILD-PLAN 5); a no-op off the naomi layout.
  const track = (event, props) => window.NaomiPDP?.track(event, props);

  // Ring sizer. Shopify always has a variant selected and the free-sizer value
  // comes first, so a bare product URL would silently start the sizer flow.
  // Until a size or the sizer row (Liquid marks its radio
  // .nj-size-dropdown__sizer-input) is picked, the trigger reads "Select a
  // size" and Add to Bag reads "Select a ring size" and opens the menu instead
  // of adding. The .nj-sizer-hint link under the menu is a shortcut to the
  // sizer row. An explicit ?variant= (deep link, reload) counts as a choice,
  // unless it came from a collection colour card (&nj_card=1): that variant
  // picks the colour and is the sizer by default, so it is not a size pick.
  // Bound once per page: this block sits inside the define guard, so
  // re-renders never stack listeners.
  const ringSizer = {
    // Keyed by product-info's data-url (the product's own /products/<handle>),
    // not location.pathname: product-info rewrites /collections/x/products/y to
    // /products/y on the first option change, and a combined-listing swap to
    // another product should start over. undefined = not read from the URL yet.
    chosenUrl: undefined,
    reveal: false,
    focusTrigger: false,

    productUrl() {
      return this.input()?.closest('product-info')?.dataset.url;
    },

    get chosen() {
      const productUrl = this.productUrl();
      if (this.chosenUrl === undefined) {
        const params = new URLSearchParams(window.location.search);
        this.chosenUrl = params.has('variant') && !params.has('nj_card') ? productUrl : null;
      }
      return !!productUrl && this.chosenUrl === productUrl;
    },

    choose(value) {
      this.chosenUrl = value ? this.productUrl() : null;
    },

    // product-info keeps the old <variant-selects> hidden for 500ms after a swap.
    input() {
      return Array.from(document.querySelectorAll('variant-selects input.nj-size-dropdown__sizer-input')).find(
        (input) => input.closest('variant-selects').style.display !== 'none'
      );
    },

    setUrl(url) {
      if (url.href === window.location.href) return;
      window.history.replaceState(window.history.state, '', url);
      document.querySelector('share-button')?.updateUrl?.(`${window.shopUrl}${url.pathname}${url.search}`);
    },

    apply() {
      const sizer = this.input();
      if (!sizer) return;
      const chosen = this.chosen;
      const on = chosen && sizer.checked;
      const form = document.getElementById(sizer.getAttribute('form'));
      const dropdown = sizer.closest('size-dropdown');
      dropdown?.syncTrigger?.();
      const hint = sizer.closest('fieldset')?.querySelector('.nj-sizer-hint');
      if (hint) hint.hidden = on;
      // NAO-97: the confirmation line with Change stands in for the hint.
      const confirm = sizer.closest('fieldset')?.querySelector('.pdp-sizer-chosen');
      if (confirm) confirm.hidden = !on;
      if (this.focusTrigger) dropdown?.trigger?.focus();
      this.focusTrigger = false;

      document.dispatchEvent(new CustomEvent('nj:sizer', { detail: { on, reveal: on && this.reveal } }));
      this.reveal = false;

      // The sticky bar mirrors this button's label, so one write covers both.
      const submit = form?.querySelector('[type="submit"]');
      const label = submit?.querySelector('span');
      const pick = window.variantStrings?.ringSizeRequired; // layout/theme.liquid
      if (!label || !pick || submit.hasAttribute('disabled')) return;
      if (!chosen) label.textContent = pick;
      else if (label.textContent.trim() === pick) label.textContent = window.variantStrings.addToCart; // server-rendered label is padded
    },

    // product-info writes ?variant= on every option change, including a colour
    // change before any size is picked; that must not read as a choice on
    // reload, but the colour should survive it (Back, reload, shared link). So
    // unpicked keeps ?variant= and adds nj_card=1, the same "colour, not a size"
    // marker collection cards use; a pick (the sizer included) drops it. Called only
    // once the form's variant id is current (variantChange, a fresh menu, or a
    // pick that needed no fetch), never mid-fetch.
    syncUrl() {
      const sizer = this.input();
      if (!sizer || sizer.closest('product-info')?.dataset.updateUrl !== 'true') return;
      const url = new URL(window.location.href);
      const variantId = document.getElementById(sizer.getAttribute('form'))?.querySelector('input[name="id"]')?.value;
      if (variantId) url.searchParams.set('variant', variantId);
      if (this.chosen || !url.searchParams.has('variant')) url.searchParams.delete('nj_card');
      else url.searchParams.set('nj_card', '1');
      this.setUrl(url);
    },

    select() {
      const sizer = this.input();
      if (!sizer) return;
      this.choose(true);
      this.reveal = true;
      // The hint link hides, so focus moves to the trigger. On a re-render the
      // flag is set after click(): onPick's synchronous apply() would otherwise
      // spend it on the old trigger, and the new menu's apply() uses it instead.
      if (!sizer.checked) {
        this.viaHint = true;
        sizer.click(); // change event: product-info re-renders
        this.focusTrigger = true;
        return;
      }
      this.focusTrigger = true;
      track('sizer_selected', { via: 'hint' });
      this.apply();
      this.syncUrl();
    },

    // NAO-97 Change: back to nothing picked (the sizer row stays the checked
    // default, as on a fresh page), focus on the menu.
    clear() {
      if (!this.input()) return;
      this.choose(false);
      this.focusTrigger = true;
      this.apply();
      this.syncUrl();
      track('sizer_changed');
    },

    init() {
      // Any pick in the ring-size list is a choice. click, not only change:
      // the sizer row is checked by default, and re-picking an already-checked
      // row fires no change (so the menu closes here too, unless arrow keys).
      const onPick = (event) => {
        const input = event.target;
        if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
        const dropdown = input.closest('size-dropdown');
        if (!dropdown || !dropdown.querySelector('input.nj-size-dropdown__sizer-input')) return;
        this.choose(true);
        this.apply();
        if (event.type !== 'click') return;
        const viaHint = this.viaHint;
        this.viaHint = false;
        if (input.classList.contains('nj-size-dropdown__sizer-input')) track('sizer_selected', { via: viaHint ? 'hint' : 'menu' });
        else track('size_selected', { size: input.value });
        if (dropdown.isOpen && !dropdown.keyboardNav) dropdown.setOpen(false);
        // A re-pick of the checked row fetches nothing, so no variantChange will
        // sync the URL. A real change disables the submit synchronously while
        // product-info fetches; skip then and let variantChange do it.
        setTimeout(() => {
          const form = document.getElementById(input.getAttribute('form'));
          if (!form?.querySelector('[type="submit"]')?.hasAttribute('disabled')) this.syncUrl();
        });
      };
      document.addEventListener('click', onPick, true);
      document.addEventListener('change', onPick, true);

      document.addEventListener('click', (event) => {
        if (event.target.closest?.('.pdp-sizer-change')) this.clear();
        else if (event.target.closest?.('.nj-sizer-hint__link')) this.select();
      });

      // Capture on document runs before product-form's own submit handler.
      document.addEventListener(
        'submit',
        (event) => {
          const sizer = this.input();
          // getAttribute: form.id is shadowed by the form's <input name="id">.
          if (this.chosen || !sizer || event.target.getAttribute('id') !== sizer.getAttribute('form')) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          const cta = event.submitter?.closest?.('[data-pdp-bar-cta]');
          track('cta_redirected_to_size', {
            surface: cta ? (cta.classList.contains('pdp-peek__btn') ? 'peek' : 'bar') : 'buybox',
          });
          const dropdown = sizer.closest('size-dropdown');
          if (!dropdown) return;
          const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          dropdown.scrollIntoView({ block: 'center', behavior: smooth ? 'smooth' : 'auto' });
          dropdown.setOpen(true);
          // Nothing picked: start on the first size, not the default-checked sizer.
          dropdown.querySelector('input[type="radio"]:not(.nj-size-dropdown__sizer-input)')?.focus();
        },
        true
      );

      // product-form rewrites the label to "Add to Bag" on every variant change.
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.variantChange, () => {
          this.apply();
          this.syncUrl();
        });
      }
    },
  };
  ringSizer.init();

  customElements.define(
    'size-dropdown',
    class SizeDropdown extends HTMLElement {
      connectedCallback() {
        this.trigger = this.querySelector('.nj-size-dropdown__trigger');
        this.valueEl = this.querySelector('.nj-size-dropdown__current');
        if (!this.trigger || !this.valueEl) return;
        this.key = this.trigger.getAttribute('aria-controls');

        this.syncTrigger();
        ringSizer.apply();
        ringSizer.syncUrl();
        if (reopenFor === this.key) {
          reopenFor = null;
          this.setOpen(true);
        }

        this.trigger.addEventListener('click', () => this.setOpen(!this.isOpen));
        this.addEventListener('change', () => {
          this.syncTrigger();
          if (this.keyboardNav) {
            reopenFor = this.key; // stay open through the re-render
            return;
          }
          this.setOpen(false);
        });
        this.addEventListener('keydown', (event) => {
          if (!this.isOpen) return;
          if (event.key.startsWith('Arrow')) {
            this.keyboardNav = true;
            return;
          }
          if (event.key === 'Escape' || event.key === 'Enter') {
            event.preventDefault();
            this.setOpen(false);
            this.trigger.focus();
          }
        });
        this.onDocumentClick = (event) => {
          if (!this.contains(event.target)) this.setOpen(false);
        };
        document.addEventListener('click', this.onDocumentClick);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
      }

      get isOpen() {
        return this.trigger.getAttribute('aria-expanded') === 'true';
      }

      setOpen(open) {
        this.trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
        this.classList.toggle('nj-size-dropdown--open', open);
        this.keyboardNav = false;
        if (!open) return;
        const focusTarget = this.querySelector('input:checked') || this.querySelector('input');
        if (focusTarget) focusTarget.focus();
      }

      syncTrigger() {
        const checked = this.querySelector('input:checked');
        if (!checked) return;
        // A ring menu with a sizer row reads "Select a size" until something is picked.
        const placeholder = this.trigger.dataset.placeholder;
        const unpicked = !!placeholder && !ringSizer.chosen;
        this.valueEl.classList.toggle('nj-size-dropdown__current--placeholder', unpicked);
        if (unpicked) {
          this.valueEl.textContent = placeholder;
          this.valueEl.classList.remove('nj-size-dropdown__current--unavailable');
          return;
        }
        const text = this.querySelector(`label[for="${CSS.escape(checked.id)}"] .nj-size-dropdown__text`);
        // innerHTML, not textContent: a long value's label carries a second
        // muted line in a nested span, and the trigger mirrors it verbatim.
        // Server-rendered markup from this same element — nothing user-supplied.
        if (text) this.valueEl.innerHTML = text.innerHTML;
        else this.valueEl.textContent = checked.value;
        this.valueEl.classList.toggle('nj-size-dropdown__current--unavailable', checked.classList.contains('disabled'));
      }
    }
  );
}
